import { MessageTooltip, pxToRem, useTheme } from '@archipro-design/aria';
import { useStyles } from '@archipro-design/aria';
import type { Position, TagType } from '~/modules/product-tagging/type';
import * as S from './TagWithModals.style';

import useModalPosition from '../../hooks/use-modal-position';
import { useProductTagging } from '../../context';
import ProductTaggingModal from '../product-tagging-modal/ProductTaggingModal';
import { useTagModals } from './hook/use-tag-modals';
import TagMarker from '@modules/product-tagging/components/tag-with-modals/TagMarker';
import { useToaster } from '~/modules/root/context/ToasterContext';
import { useEffect } from 'react';
import {
    PRODUCT_TAG_WITH_MODAL_WIDTH,
    PRODUCT_TAGGING_MODAL_OFFSET_FROM_TAG,
} from '@modules/product-tagging/constant';
import useModalPlacement from '@modules/product-tagging/hooks/use-modal-placement';
import HelpGuideStepTwo from './HelpGuideStepTwo';
import { useModulesContext } from '~/modules/page-builder/context/modules-context';
import { useUser } from '~/modules/user';
import { useHelpState } from '../../hooks/use-help-state';
import { getScrollParams } from '~/modules/page-builder/util/get-scroll-params';
import { TagHoverContent } from './TagHoverContent';

interface TagProps {
    marker: TagType;
    tagPosition: Position;
    boxWidth: number;
    showImageOverlay?: boolean;
    boxRef: Element;
    deleteTag: (tagId: number) => void;
    onTagUpdated?: (tagId: number, approved: boolean) => void;
}

const TagWithModals = ({
    marker,
    tagPosition,
    boxWidth,
    showImageOverlay = false,
    boxRef,
    deleteTag,
    onTagUpdated,
}: TagProps) => {
    const {
        state: { isDeletingTag, isAddingSupplier },
        dispatch,
    } = useProductTagging();

    const {
        state: { showApproveTagHelp, approveTagHelpTagID },
        dispatch: modulesContextDispatch,
    } = useModulesContext();

    const user = useUser();
    const { addMember } = useHelpState();

    const { toast } = useToaster();

    const tagId = marker.id;
    const onApproveClicked = () => {
        if (canShowProductTile) {
            addMember(user);
        }
    };
    const onDeleteClicked = () => {
        if (canShowProductTile) {
            addMember(user);
        }
    };

    const {
        isModalOpen,
        isMarkerApproved,
        isCompletedTogglingApproved,
        showModal,
        canShowProductTile,
        bottomActions,
        confirmationMessage,
        onDeleteTag,
        setIsOpen,
        hasProduct,
    } = useTagModals({ marker, deleteTag, onApproveClicked, onDeleteClicked });

    const placement = useModalPlacement(
        tagPosition,
        PRODUCT_TAG_WITH_MODAL_WIDTH + PRODUCT_TAGGING_MODAL_OFFSET_FROM_TAG,
        boxWidth
    );

    const {
        setReferenceElement,
        setPopperElement,
        styles,
        attributes,
        update,
    } = useModalPosition(boxRef, placement);

    // Each time a new modal opens, update the position of the popper
    useEffect(() => {
        if (isModalOpen && update) {
            update();
        }
    }, [update, isModalOpen]);

    const { css } = useStyles({ showHover: isModalOpen });
    const theme = useTheme();

    const product = marker.product;

    let isShowingHelp = showApproveTagHelp && marker.id === approveTagHelpTagID;

    const { scrollToTagID } = getScrollParams(
        window?.top?.location?.search ?? ''
    );

    const openTagByDefault =
        !isShowingHelp && scrollToTagID === marker.id ? true : false;

    let tagVisibility = {};
    if (isShowingHelp === true || openTagByDefault) {
        tagVisibility = {
            visibility: 'visible',
            opacity: 1,
        };
    }

    // if openTagByDefault is true, then display modal when page loads
    useEffect(() => {
        if (openTagByDefault) {
            setIsOpen(true);
        }
    }, [openTagByDefault, setIsOpen]);

    useEffect(() => {
        if (!isCompletedTogglingApproved) {
            return;
        }
        onTagUpdated?.(tagId, isMarkerApproved);
        toast(isMarkerApproved ? 'Tag approved' : 'Tag un-approved');
    }, [
        isCompletedTogglingApproved,
        isMarkerApproved,
        tagId,
        toast,
        onTagUpdated,
    ]);

    return (
        <div
            style={{
                top: `${tagPosition.y}%`,
                left: `${tagPosition.x}%`,
                ...tagVisibility,
            }}
            className={`${css(S.TagModalsContainer)} product-tagging-tag`}
            ref={setReferenceElement}
            onMouseEnter={() => {
                // we don't want to show the modal if the help guide is open
                if (!isShowingHelp) {
                    setIsOpen(true);
                }
            }}
            onMouseLeave={() => setIsOpen(false)}
        >
            <MessageTooltip
                open={isShowingHelp}
                trigger={
                    <TagMarker
                        isApproved={isMarkerApproved}
                        isActive={isModalOpen}
                    />
                }
                content={{
                    children: (
                        <HelpGuideStepTwo
                            onClose={() => {
                                addMember(user);
                                modulesContextDispatch({
                                    type: 'resetApproveTagHelp',
                                });
                            }}
                        />
                    ),
                    color: 'white',
                    padding: 0,
                    variables: {
                        maxWidth: pxToRem(400),
                    },
                    className: css(
                        tagPosition.x <= 50
                            ? S.TooltipContentAfter
                            : S.TooltipContentBefore
                    ),
                }}
                position={tagPosition.x <= 50 ? 'after' : 'before'}
                offset={[0, 12]}
                pointing={true}
            />
            {isModalOpen &&
                canShowProductTile &&
                !isShowingHelp &&
                product?.id && (
                    <div
                        ref={setPopperElement}
                        style={styles.popper}
                        {...attributes.popper}
                        className={css(S.ProductTagTileContainer)}
                    >
                        <TagHoverContent
                            bottomActions={bottomActions}
                            showImageOverlay={showImageOverlay}
                            marker={marker}
                        />
                    </div>
                )}
            {showModal && (
                <div
                    style={{
                        ...styles.popper,
                        zIndex: theme.siteVariables.zIndexes.overlay,
                    }}
                    ref={setPopperElement}
                    {...attributes.popper}
                >
                    <ProductTaggingModal
                        onCancel={() => {
                            setIsOpen(false);
                            isAddingSupplier &&
                                dispatch({
                                    type: 'TOGGLE_ADDING_SUPPLIER',
                                    payload: false,
                                });
                        }}
                        onConfirm={onDeleteTag}
                        title={hasProduct ? 'Tag Saved' : undefined}
                        message={confirmationMessage}
                        disableFooterButton={isDeletingTag}
                    />
                </div>
            )}
        </div>
    );
};

export default TagWithModals;
